import { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Button, Portal } from '@nazka/nazka.mapframe.components'
import { useImageViewer } from 'react-image-viewer-hook'
import { faChevronDown, faChevronUp, faLocation } from '@fortawesome/pro-light-svg-icons'

import LazyImage from '@App/components/LazyImage/LazyImage'
import { setHoveredItem } from '@App/pages/MosquitoObservations/mosquito-observations.slice'

import { getCertaintyStatus } from './mosquito-observations-sightings-list-item.utils'

import './mosquito-observations-sightings-list-item.styl'

function getSightingId(sighting) {
  return sighting?.id
}

function getSightingSource(sighting) {
  return sighting?.properties?.source || 'Unknown'
}

function getSightingCreationTime(sighting) {
  return sighting?.properties?.creationTime
}

function MosquitoObservationsSightingsListItem({ map, specimen }) {
  const { getOnClick, ImageViewer } = useImageViewer()
  const dispatch = useDispatch()

  const { photoUrl, publicNote, confidenceLabel, name, color } = specimen?.properties?.identification || {}

  const hoveredItem = useSelector(state => state.mosquitoDetection.hoveredItem)
  const isHovered = hoveredItem && getSightingId(hoveredItem) === getSightingId(specimen) && getSightingSource(hoveredItem) === getSightingSource(specimen)

  const [isExpanded, setIsExpanded] = useState(false)

  const { label, color: confidenceColor } = getCertaintyStatus(confidenceLabel)

  return (
    <div
      className={`mosquito-observations-sightings-list-item${isHovered ? ' mosquito-observations-sightings-list-item--hovered' : ''}`}
      onMouseEnter={() => dispatch(setHoveredItem(specimen))}
      onMouseLeave={() => dispatch(setHoveredItem(null))}
      id={(`specimen-${getSightingSource(specimen)}-${getSightingId(specimen)}`).toLowerCase()}
    >
      <div className="mosquito-observations-sightings-list-item__head">
        <div className="mosquito-observations-sightings-list-item__head--type">
          <span className="color-indicator" style={{ backgroundColor: color }}></span>
          <div className="mosquito-observations-sightings-list-item__head--type__text">
            <span className="mosquito-observations-sightings-list-item__head--type__text__name">
              {name || 'Unclassified'}
            </span>
            <span className="mosquito-observations-sightings-list-item__head--type__text__species" style={{ color: confidenceColor }}>
              {label}
            </span>
          </div>
        </div>
        <div className="mosquito-observations-sightings-list-item__head__actions">
          <Button
            className="mosquito-observations-sightings-list-item__head__actions__action"
            icon={faLocation}
            layout="ghost"
            hover={false}
            onClick={() => {
              const coordinates = specimen?.geometry?.coordinates
              if (!map || !Array.isArray(coordinates) || coordinates.length < 2) return

              map.flyTo({
                center: coordinates,
                zoom: 14,
                essential: true,
              })
            }}
          />
          <Button
            className="mosquito-observations-sightings-list-item__head__actions__action"
            icon={isExpanded ? faChevronUp : faChevronDown}
            layout="ghost"
            hover={false}
            onClick={() => setIsExpanded(!isExpanded)}
          />
        </div>
      </div>
      <div className={`mosquito-observations-sightings-list-item__content${isExpanded ? ' mosquito-observations-sightings-list-item__content--expanded' : ''}`}>
        <div className="mosquito-observations-sightings-list-item__content__inner">
          <div className="mosquito-observations-sightings-list-item__content--source">
            <p>Source:</p>
            <p>{getSightingSource(specimen)}</p>
          </div>
          <hr />
          <div className="mosquito-observations-sightings-list-item__content--date">
            <p>Date:</p>
            <p>{new Date(getSightingCreationTime(specimen)).toLocaleString()}</p>
          </div>
          <hr />
          {photoUrl && (
            <>
              <div className="mosquito-observations-sightings-list-item__content--photo">
                <p>Photo:</p>
                <a
                  key={photoUrl}
                  href={`${photoUrl}?auto=compress&cs=tinysrgb&w=1200`}
                  onClick={getOnClick(`${photoUrl}?auto=compress&cs=tinysrgb&w=1200`)}
                  className="mosquito-observations-sightings-list-item__content--photo-src"
                >
                  <LazyImage
                    src={photoUrl}
                    alt="Specimen photo"
                    className="mosquito-observations-sightings-list-item__content--photo-src"
                    height="200px"
                  />
                </a>
                <Portal>
                  <ImageViewer />
                </Portal>
              </div>
              <hr />
            </>
          )}
          <div className="mosquito-observations-sightings-list-item__content--validation">
            <p>Expert validation:</p>
            <p style={{ color: confidenceColor }}>{label}</p>
          </div>
          {publicNote && (
            <>
              <hr />
              <div className="mosquito-observations-sightings-list-item__content--note">
                <p>Expert note: </p>
                <p>{publicNote || '/'}</p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default MosquitoObservationsSightingsListItem
